import { SxProps, Theme } from "@mui/material";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { focusRing, menu, signer } from "theme/colors";

/**
 * Geometry of the phone field. Colours come from `theme.palette.formField.*`
 * (and the app-wide `focusRing` token for the country select's keyboard
 * focus ring); only sizes/spacing live here so the control stays in step
 * with the rest of the form system (`CustomInput`'s box, `FormField.tokens`).
 */
export const contactNumberMetrics = {
  height: formFieldMetrics.height,
  radius: formFieldMetrics.radius,
  paddingX: formFieldMetrics.paddingX,
  fontSize: formFieldMetrics.fontSize,
  fontWeight: formFieldMetrics.fontWeight,
  helperFontSize: formFieldMetrics.helperFontSize,
  /** Width of the collapsed, flag-only country selector. Wide enough that
   * the dropdown arrow (reserved via the select's right padding) doesn't
   * overlap the flag. */
  countrySelectWidth: "60px",
} as const;

const m = contactNumberMetrics;

const customContactNumberStyles = {
  /** Same label as `CustomInput`'s, so a phone field sits level with its row. */
  inputLabel: ((theme: Theme) => ({
    textAlign: "left",
    ml: 0,
    flexShrink: 0,
    color: theme.palette.formField.label,
    fontSize: formFieldMetrics.labelFontSize,
    fontWeight: formFieldMetrics.labelFontWeight,
    lineHeight: "normal",
    "& .MuiFormLabel-asterisk": {
      color: theme.palette.formField.required,
      ml: formFieldMetrics.asteriskGap,
    },
  })) as SxProps<Theme>,

  /** The field box — same chrome as every other text input in the app. */
  textField: ((theme: Theme) => ({
    "& .MuiOutlinedInput-root": {
      height: m.height,
      p: 0,
      borderRadius: m.radius,
      bgcolor: theme.palette.formField.bg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.formField.border,
        borderWidth: "1px",
      },
      "&:hover": {
        bgcolor: theme.palette.formField.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.formField.border,
        },
      },
      "&.Mui-focused": {
        bgcolor: theme.palette.formField.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.formField.focusBorder,
          borderWidth: "1px",
        },
      },
      "&.Mui-disabled": {
        bgcolor: theme.palette.formField.disabledBg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.formField.disabledBorder,
        },
      },
      // Keep the validation outline visible — the rules above are more
      // specific than MUI's own error styling.
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      p: `0 ${m.paddingX} 0 8px`,
      fontSize: m.fontSize,
      fontWeight: m.fontWeight,
      color: theme.palette.formField.text,
      "&::placeholder": {
        color: theme.palette.formField.placeholder,
        opacity: 1,
        fontSize: m.fontSize,
        fontWeight: m.fontWeight,
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: theme.palette.formField.disabledText,
        color: theme.palette.formField.disabledText,
      },
    },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "4px",
      fontSize: m.helperFontSize,
    },
  })) as SxProps<Theme>,

  /** Adornment wrapper holding the flag-only country selector. */
  adornment: {
    mr: "2px",
  } as SxProps<Theme>,

  /** Collapsed country-code `Select` — shows just the flag, no border. */
  countrySelect: ((theme: Theme) => ({
    width: m.countrySelectWidth,
    flexShrink: 0,
    fieldset: { display: "none !important" },
    "& .MuiOutlinedInput-notchedOutline": { border: "none !important" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      display: "none !important",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: "unset",
      boxSizing: "border-box",
      height: m.height,
      // Right padding reserves room for the dropdown arrow so it doesn't
      // sit on top of the flag (the arrow is absolutely positioned).
      p: "0 20px 0 10px !important",
      // The app's own keyboard focus ring (theme/colors.ts `focusRing`) —
      // native `:focus-visible` already tells keyboard and mouse activation
      // apart, so no extra "input mode" tracking is needed to gate it.
      "&:focus-visible": {
        outline: `2px solid ${focusRing}`,
        outlineOffset: "1px",
        borderRadius: "6px",
      },
    },
    "& .MuiSelect-icon": {
      color: theme.palette.formField.adornment,
      right: "2px",
      fontSize: "18px",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  })) as SxProps<Theme>,

  /**
   * Country row inside the dropdown. Font, padding, hover and selected fill
   * all come from the global `MuiMenuItem` theme (`menu.*`), like every other
   * menu in the app.
   */
  countryOption: {
    gap: menu.itemGap,
  } as SxProps<Theme>,

  /** The selected country while a search filters it out — mounted, not shown. */
  countryOptionHidden: {
    display: "none",
  } as SxProps<Theme>,

  countryName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  countryDialCode: {
    flexShrink: 0,
    color: signer.text.meta,
    fontWeight: 500,
  } as SxProps<Theme>,

  /** The search header sits flush with the top of the menu as it scrolls. */
  countryMenuList: {
    pt: 0,
  } as SxProps<Theme>,

  /**
   * Sticky search header, laid out like the Sign Requests tag picker's: the
   * shared form field in a padded band with a divider under it. The negative
   * margin cancels the menu list's own side padding so the divider runs edge
   * to edge.
   */
  countrySearchHeader: ((theme: Theme) => ({
    bgcolor: theme.palette.background.paper,
    mx: `-${menu.padding}`,
    mb: menu.padding,
    p: "10px",
    borderBottom: `1px solid ${signer.border.light}`,
    lineHeight: "normal",
    zIndex: 1,
  })) as SxProps<Theme>,

  countrySearchIcon: {
    fontSize: "17px",
    color: signer.text.faint,
  } as SxProps<Theme>,

  /** "No matching countries" — the tag picker's hint line. */
  countrySearchEmpty: {
    position: "static",
    p: "8px 11px",
    fontSize: "12.5px",
    fontWeight: 500,
    lineHeight: 1.55,
    color: signer.text.meta,
  } as SxProps<Theme>,
};

export default customContactNumberStyles;
